/**
 * NIBSSIntegrationPage — `/integration/nibss`.
 *
 * NIBSS-specific integration status, wired to
 * GET /v1/integration/nibss/health on service-audit.
 */
import { type JSX, useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { request } from '../../lib/api/client';
import { PageHeader } from '../shared/StaffUI';
import { env } from '../../lib/env';

interface NibssHealth {
  status: string;
  bvnLookup: string;
  nipTransfer: string;
  checkedAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  REACHABLE: 'bg-green-50 text-green-700',
  UNREACHABLE: 'bg-red-50 text-red-700',
  DEGRADED: 'bg-amber-50 text-amber-700',
  OPERATIONAL: 'bg-green-50 text-green-700',
  DEGRADED_OP: 'bg-amber-50 text-amber-700',
};

export function NIBSSIntegrationPage(): JSX.Element {
  const [health, setHealth] = useState<NibssHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    setLoading(true);
    try {
      const data = await request<NibssHealth>(
        env.serviceAuditBaseUrl,
        '/v1/integration/nibss/health',
      );
      setHealth(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load NIBSS health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadHealth(); }, [loadHealth]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="NIBSS integration"
        description="NIBSS gateway health — BVN lookup, NIP transfer status."
        actions={
          <button
            type="button"
            onClick={() => void loadHealth()}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-nipost-green rounded-lg disabled:opacity-50"
            data-testid="refresh-nibss-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={() => setError(null)} className="ml-3 underline">dismiss</button>
        </div>
      ) : null}

      {loading ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center text-muted text-sm">
          Loading NIBSS health…
        </div>
      ) : health ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider">Gateway</p>
              <span
                className={`inline-flex mt-2 items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[health.status] ?? 'bg-gray-50 text-gray-700'}`}
                data-testid="nibss-gateway-status"
              >
                {health.status}
              </span>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider">BVN lookup</p>
              <span
                className={`inline-flex mt-2 items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[health.bvnLookup] ?? 'bg-gray-50 text-gray-700'}`}
                data-testid="nibss-bvn-status"
              >
                {health.bvnLookup}
              </span>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <p className="text-[10px] font-bold text-muted uppercase tracking-wider">NIP transfer</p>
              <span
                className={`inline-flex mt-2 items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_COLORS[health.nipTransfer] ?? 'bg-gray-50 text-gray-700'}`}
                data-testid="nibss-nip-status"
              >
                {health.nipTransfer}
              </span>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border p-4">
            <p className="text-xs text-muted">
              Last checked:{' '}
              <span className="font-mono">
                {new Date(health.checkedAt).toLocaleString()}
              </span>
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}
